import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CollaborationInvite } from '../../types/collaboration';
import { inviteCollaborator } from '../../services/api';

interface InviteCollaboratorModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const InviteCollaboratorModal: React.FC<InviteCollaboratorModalProps> = ({
  projectId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingInvites, setPendingInvites] = useState<CollaborationInvite[]>([]);
  const [cancellingInviteId, setCancellingInviteId] = useState<string | null>(null);

  // Fetch pending invites for this project
  useEffect(() => {
    if (!isOpen || !projectId) {
      return;
    }

    // Query pending invites for this project
    const q = query(
      collection(db, 'collaborationInvites'),
      where('projectId', '==', projectId),
      where('status', '==', 'pending')
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const invitesList: CollaborationInvite[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            inviteId: doc.id,
            projectId: data.projectId,
            fromUserId: data.fromUserId,
            fromUserEmail: data.fromUserEmail,
            toEmail: data.toEmail,
            status: data.status,
            createdAt: data.createdAt instanceof Timestamp
              ? data.createdAt.toDate()
              : new Date(data.createdAt),
            respondedAt: data.respondedAt instanceof Timestamp
              ? data.respondedAt.toDate()
              : data.respondedAt ? new Date(data.respondedAt) : undefined,
          };
        });

        setPendingInvites(invitesList);
      },
      (err) => {
        console.error('Error fetching pending invites:', err);
      }
    );

    return () => unsubscribe();
  }, [isOpen, projectId]);

  // Validate email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validate email
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!isValidEmail(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      await inviteCollaborator(projectId, email.trim().toLowerCase());
      setSuccessMessage(`Invitation sent to ${email.trim()}`);
      setEmail('');

      // Call onSuccess after a brief delay to show success message
      setTimeout(() => {
        setSuccessMessage(null);
        onSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Error inviting collaborator:', err);

      // Extract user-friendly error message
      let errorMessage = 'Failed to send invitation. Please try again.';
      if (err.message) {
        if (err.message.includes('already a collaborator')) {
          errorMessage = 'This user is already a collaborator on this project';
        } else if (err.message.includes('already invited')) {
          errorMessage = 'This user has already been invited';
        } else if (err.message.includes('owner')) {
          errorMessage = 'Cannot invite the project owner';
        }
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel invite
  const handleCancelInvite = async (inviteId: string) => {
    setCancellingInviteId(inviteId);
    setError(null);

    try {
      // Update invite status to 'cancelled'
      const inviteRef = doc(db, 'collaborationInvites', inviteId);
      await updateDoc(inviteRef, {
        status: 'cancelled',
        respondedAt: new Date(),
      });
    } catch (err) {
      console.error('Error cancelling invite:', err);
      setError('Failed to cancel invitation. Please try again.');
    } finally {
      setCancellingInviteId(null);
    }
  };

  // Format relative time (e.g., "2 days ago")
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="bg-white px-6 pt-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Invite Collaborator</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="ml-3 text-sm text-green-700">{successMessage}</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Invite Form */}
            <form onSubmit={handleSubmit} className="mb-6">
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  className="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  disabled={isSubmitting}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="w-full inline-flex justify-center items-center px-4 py-2 bg-primary-500 text-white rounded-md font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Invite'
                )}
              </button>
            </form>

            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Pending Invitations</h4>
                <div className="space-y-2">
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.inviteId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {invite.toEmail}
                        </p>
                        <p className="text-xs text-gray-500">
                          Sent {getRelativeTime(invite.createdAt)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCancelInvite(invite.inviteId)}
                        disabled={cancellingInviteId === invite.inviteId}
                        className="ml-3 inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {cancellingInviteId === invite.inviteId ? 'Cancelling...' : 'Cancel'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
